// 전역 확인 모달 (window.confirm 대체)
import React, { createContext, useCallback, useContext, useState } from "react";
import { BTN_DANGER, BTN_PRIMARY, BTN_SECONDARY } from "../constants/ui";

interface ConfirmOptions {
  message: string;
  /** 확인 버튼 텍스트 (기본: "확인") */
  confirmText?: string;
  /** 취소 버튼 텍스트 (기본: "취소") */
  cancelText?: string;
  /** danger 스타일 확인 버튼 사용 여부 */
  danger?: boolean;
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<{
    open: boolean;
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({ open: true, options, resolve });
    });
  }, []);

  const handleResult = (value: boolean) => {
    state?.resolve(value);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state?.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => handleResult(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="whitespace-pre-line text-sm text-slate-700 leading-relaxed">
              {state.options.message}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className={BTN_SECONDARY}
                onClick={() => handleResult(false)}
              >
                {state.options.cancelText ?? "취소"}
              </button>
              <button
                type="button"
                className={state.options.danger ? BTN_DANGER : BTN_PRIMARY}
                onClick={() => handleResult(true)}
              >
                {state.options.confirmText ?? "확인"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmContextValue => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
};
